import React from 'react';
import { Form, Input, InputNumber, DatePicker, Radio, Row, Col } from 'antd';
import dayjs from 'dayjs';
import LookupField from '../../../components/LookupField';
import { J212FormData } from '../hooks/useJ212Record';

interface Props {
  formData: J212FormData;
  isEditing: boolean;
  isEditMode?: boolean; // true when mode === 'edit' (not add)
  setField: <K extends keyof J212FormData>(field: K, value: J212FormData[K]) => void;
}

const Tab1AlbumInfo: React.FC<Props> = ({ formData, isEditing, isEditMode, setField }) => {
  return (
    <Form layout="vertical" style={{ maxWidth: 700 }}>
      {/* Row 1: fj210_18 專輯類別 — optiongroup (ButtonCount=2) → Radio.Group */}
      <Form.Item label="專輯類別" required>
        {isEditing ? (
          <Radio.Group
            data-contract-field="fj210_18"
            value={formData.fj210_18}
            onChange={e => setField('fj210_18', e.target.value)}
          >
            <Radio value={1}>歐美</Radio>
            <Radio value={2}>日本</Radio>
          </Radio.Group>
        ) : (
          <span>{formData.fj210_18 || '—'}</span>
        )}
      </Form.Item>

      {/* Row 2: 專輯編號 (left) | 交易條件 (right) — FoxPro two-column layout */}
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item label="專輯編號">
            <Input
              value={formData.fj210_01 || (isEditing && !isEditMode ? '(自動產生)' : '')}
              disabled
              style={{ maxWidth: 180 }} data-contract-field="fj210_01"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="交易條件" required>
            {isEditing ? (
              <LookupField
                lookupType="tradeTerms"
                value={(formData.fj210_17 as string) || ''}
                onChange={(code) => setField('fj210_17', code)}
                disabled={false}
                title="搜尋交易條件" contractFieldId="fj210_17"
              />
            ) : (
              <span>{formData.fj210_17 || '—'}</span>
            )}
          </Form.Item>
        </Col>
      </Row>

      {/* Row 3: 製作日期 (left) | 預估匯率 (right) */}
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item label="製作日期" required>
            {isEditing && !isEditMode ? (
              <DatePicker
                data-contract-field="fj210_11"
                value={formData.fj210_11 ? dayjs(formData.fj210_11) : null}
                onChange={d => setField('fj210_11', d ? d.format('YYYY-MM-DD') : '')}
                style={{ width: '100%' }}
              />
            ) : (
              <span>{formData.fj210_11 || '—'}</span>
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="預估匯率" required>
            {isEditing ? (
              <InputNumber
                value={formData.fj210_16}
                onChange={v => setField('fj210_16', v ?? 0)}
                min={0}
                precision={4}
                style={{ width: '100%' }} data-contract-field="fj210_16"
              />
            ) : (
              <span>{formData.fj210_16 ?? '—'}</span>
            )}
          </Form.Item>
        </Col>
      </Row>

      {/* Row 4: fj210_12 專輯主題 — editbox → TextArea (spans full width) */}
      <Form.Item label="專輯主題">
        {isEditing ? (
          <Input.TextArea
            data-contract-field="fj210_12"
            value={formData.fj210_12}
            onChange={e => setField('fj210_12', e.target.value)}
            rows={3}
            placeholder="專輯主題"
          />
        ) : (
          <span style={{ whiteSpace: 'pre-wrap' }}>{formData.fj210_12 || '—'}</span>
        )}
      </Form.Item>

      {/* Row 5: fj210_15 公司名稱 — textbox (full width) */}
      <Form.Item label="公司名稱">
        {isEditing ? (
          <Input
            value={formData.fj210_15}
            onChange={e => setField('fj210_15', e.target.value)}
            placeholder="公司名稱"
            maxLength={30} data-contract-field="fj210_15"
          />
        ) : (
          <span>{formData.fj210_15 || '—'}</span>
        )}
      </Form.Item>

      {/* Row 6-7: 製作人員 | 製作份數 — side by side */}
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item label="製作人員" required>
            {isEditing && !isEditMode ? (
              <LookupField
                lookupType="employee"
                value={(formData.fj210_13 as string) || ''}
                onChange={(code) => setField('fj210_13', code)}
                disabled={false}
                title="搜尋製作人員" contractFieldId="fj210_13"
              />
            ) : (
              <span>{formData.fj210_13 || '—'}</span>
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="製作份數">
            {isEditing ? (
              <InputNumber
                value={formData.fj210_14}
                onChange={v => setField('fj210_14', v ?? 0)}
                min={0}
                max={999}
                precision={0}
                style={{ width: '100%' }} data-contract-field="fj210_14"
              />
            ) : (
              <span>{formData.fj210_14 ?? '—'}</span>
            )}
          </Form.Item>
        </Col>
      </Row>

      {/* Row 8: fj210_23 專輯抬頭日期 — textbox */}
      <Form.Item label="專輯抬頭日期" required>
        {isEditing ? (
          <Input
            value={formData.fj210_23}
            onChange={e => setField('fj210_23', e.target.value)}
            placeholder="專輯抬頭日期"
            maxLength={10}
            style={{ maxWidth: 200 }} data-contract-field="fj210_23"
          />
        ) : (
          <span>{formData.fj210_23 || '—'}</span>
        )}
      </Form.Item>
    </Form>
  );
};

export default Tab1AlbumInfo;
